import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { contentApi } from '../lib/api';
import { ArrowLeft, Clock, Radio, Video, CheckCircle } from 'lucide-react';

const PREP_TIPS = [
  'Wear loose, comfortable clothing',
  'Clear a 6 × 4 ft space around you',
  'Keep water nearby',
  'Warm up with 5 minutes of light stretching',
];

function pad(n) {
  return String(Math.max(0, n)).padStart(2, '0');
}

function formatCountdown(ms) {
  if (ms <= 0) return { h: '00', m: '00', s: '00' };
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  return { h: pad(h), m: pad(m), s: pad(s) };
}

// Allow joining from 5 minutes before start
const JOIN_WINDOW_MS = 5 * 60 * 1000;

export default function SessionLobbyPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [session, setSession] = useState(location.state?.session || null);
  const [now, setNow] = useState(Date.now());
  const [loadError, setLoadError] = useState('');

  // If arrived without state (direct URL), fetch the active session
  useEffect(() => {
    if (session) return;
    contentApi
      .getLiveSessions()
      .then((res) => {
        const sessions = res.data || [];
        const nowTs = Date.now();
        const active =
          sessions.find((s) => s.is_live_now) ||
          sessions
            .filter((s) => s.live_start_at && new Date(s.live_start_at) > nowTs)
            .sort((a, b) => new Date(a.live_start_at) - new Date(b.live_start_at))[0];
        if (active) setSession(active);
        else setLoadError('No upcoming session found.');
      })
      .catch(() => setLoadError('Could not load session details.'));
  }, [session]);

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  // The API never sends the raw join_url/recording_url — only can_join /
  // is_replay_available flags. Poll until whichever flag we're waiting on
  // flips true, so the button activates without a page refresh.
  useEffect(() => {
    if (!session?.day) return;
    const needsJoinFlag = sessionState !== 'ended' && !session.can_join;
    const needsReplayFlag = sessionState === 'ended' && !session.is_replay_available;
    if (!needsJoinFlag && !needsReplayFlag) return;

    const id = setInterval(() => {
      contentApi
        .getLiveSessions()
        .then((res) => {
          const updated = (res.data || []).find((s) => s.day === session.day);
          if (updated) setSession((prev) => (prev ? { ...prev, ...updated } : prev));
        })
        .catch(() => {});
    }, 15000);

    return () => clearInterval(id);
  }, [session?.day, session?.can_join, session?.is_replay_available, sessionState]);

  const startMs = session?.live_start_at ? new Date(session.live_start_at).getTime() : null;
  const endMs = session?.live_end_at ? new Date(session.live_end_at).getTime() : null;

  const sessionState = useMemo(() => {
    if (!startMs || !endMs) return 'unscheduled';
    if (now < startMs) return 'upcoming';
    if (now >= startMs && now <= endMs) return 'live';
    return 'ended';
  }, [now, startMs, endMs]);

  const msUntilStart = startMs ? Math.max(startMs - now, 0) : 0;
  const countdown = formatCountdown(msUntilStart);

  // Button enabled 5 min before start or while live — can_join is the
  // server's authoritative flag; the local time check just avoids a flash
  // of an enabled button before the first fetch confirms it.
  const canJoin =
    session?.can_join &&
    (sessionState === 'live' || (sessionState === 'upcoming' && msUntilStart <= JOIN_WINDOW_MS));

  const handleJoin = () => {
    if (session?.can_join && session?.day != null) {
      window.open(contentApi.getJoinUrl(session.day), '_blank', 'noopener,noreferrer');
    }
  };

  const startTimeStr = startMs
    ? new Date(startMs).toLocaleTimeString('en-IN', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
        timeZone: 'Asia/Kolkata',
      })
    : null;

  const startDateStr = startMs
    ? new Date(startMs).toLocaleDateString('en-IN', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        timeZone: 'Asia/Kolkata',
      })
    : null;

  if (loadError) {
    return (
      <div className="min-h-screen bg-[#0F0C0A] flex items-center justify-center text-[#F8F2E8]">
        <div className="text-center space-y-4">
          <p className="text-[#E8D8BE]/70">{loadError}</p>
          <Link to="/dashboard" className="text-[#DDA15E] hover:underline text-sm flex items-center justify-center gap-1">
            <ArrowLeft className="w-4 h-4" /> Back to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="min-h-screen bg-[#0F0C0A] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#DDA15E] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0F0C0A] text-[#F8F2E8] flex flex-col">
      {/* Background radial glows */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 right-1/4 w-96 h-96 bg-[#9A3B26]/15 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-[#DDA15E]/10 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 flex flex-col min-h-screen max-w-2xl mx-auto w-full px-4 py-6">

        {/* Back nav */}
        <Link
          to="/dashboard"
          className="inline-flex items-center gap-1.5 text-sm text-[#E8D8BE]/60 hover:text-[#E8D8BE] transition-colors mb-8 self-start"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Dashboard
        </Link>

        {/* Live badge */}
        {sessionState === 'live' && (
          <div className="flex items-center gap-2 mb-4">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500" />
            </span>
            <span className="text-xs font-semibold tracking-widest uppercase text-red-400">Live Now</span>
          </div>
        )}

        {/* Session label */}
        <p className="text-xs tracking-[0.25em] uppercase text-[#DDA15E]/80 mb-2">
          Day {session.day} · Kalari Session
        </p>

        {/* Title */}
        <h1 className="text-3xl sm:text-4xl font-bold leading-tight mb-2">{session.title}</h1>

        {/* Date/time */}
        {startDateStr && (
          <p className="text-[#E8D8BE]/60 text-sm mb-8">
            {startDateStr} · {startTimeStr} IST
          </p>
        )}

        {/* Countdown block */}
        {sessionState === 'upcoming' && (
          <div className="bg-[#1F1A17]/80 border border-[#E8D8BE]/10 rounded-2xl p-6 mb-6 text-center">
            <p className="text-xs tracking-[0.2em] uppercase text-[#E8D8BE]/50 mb-4">
              <Clock className="inline w-3.5 h-3.5 mr-1.5 -mt-0.5" />
              Session starts in
            </p>
            <div className="flex items-end justify-center gap-3">
              {[{ label: 'HRS', value: countdown.h },
                { label: 'MIN', value: countdown.m },
                { label: 'SEC', value: countdown.s }].map(({ label, value }, i) => (
                <div key={label} className="flex items-end gap-3">
                  {i > 0 && <span className="text-3xl font-light text-[#DDA15E]/40 mb-1">:</span>}
                  <div className="text-center">
                    <div className="text-5xl sm:text-6xl font-bold tabular-nums tracking-tight text-[#F8F2E8]">
                      {value}
                    </div>
                    <p className="text-[10px] tracking-[0.2em] text-[#E8D8BE]/40 mt-1 uppercase">{label}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {sessionState === 'ended' && (
          <div className="bg-[#1F1A17]/80 border border-[#E8D8BE]/10 rounded-2xl p-6 mb-6 text-center">
            <p className="text-[#E8D8BE]/50 text-sm">This session has ended.</p>
            {session.is_replay_available && session.day != null && (
              <a
                href={contentApi.getReplayUrl(session.day)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 mt-3 text-sm text-[#DDA15E] hover:underline"
              >
                <Video className="w-4 h-4" /> Watch the replay
              </a>
            )}
          </div>
        )}

        {/* Description */}
        {session.description && (
          <div className="bg-[#1F1A17]/60 border border-[#E8D8BE]/10 rounded-xl p-5 mb-6">
            <p className="text-sm text-[#E8D8BE]/80 leading-relaxed">{session.description}</p>
          </div>
        )}

        {/* Preparation tips */}
        <div className="bg-[#1F1A17]/60 border border-[#E8D8BE]/10 rounded-xl p-5 mb-8">
          <p className="text-xs tracking-[0.2em] uppercase text-[#E8D8BE]/50 mb-3">Before you join</p>
          <ul className="space-y-2">
            {PREP_TIPS.map((tip) => (
              <li key={tip} className="flex items-start gap-2.5 text-sm text-[#E8D8BE]/80">
                <CheckCircle className="w-4 h-4 text-[#DDA15E] shrink-0 mt-0.5" />
                {tip}
              </li>
            ))}
          </ul>
        </div>

        {/* Join button */}
        {sessionState !== 'ended' && (
          <div className="mt-auto">
            {canJoin ? (
              <button
                onClick={handleJoin}
                className="w-full py-4 rounded-2xl bg-[#DDA15E] hover:bg-[#E4B377] text-[#1F1A17] font-bold text-lg flex items-center justify-center gap-2.5 transition-colors shadow-[0_8px_32px_rgba(221,161,94,0.3)]"
              >
                <Radio className="w-5 h-5" />
                Join Live Session
              </button>
            ) : (
              <div className="text-center space-y-2">
                <button
                  disabled
                  className="w-full py-4 rounded-2xl bg-[#E8D8BE]/10 text-[#E8D8BE]/30 font-bold text-lg cursor-not-allowed border border-[#E8D8BE]/10"
                >
                  Join opens 5 minutes before start
                </button>
                <p className="text-xs text-[#E8D8BE]/30">
                  Come back to this page — the button activates automatically
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
